<script setup>
import { reactive, computed, ref, watch, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, url } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['edit', 'create'].includes(value),
  },
  response: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();

const MB = 1024 * 1024;
const MAX_ATTACHMENTS = 5;
const ATTACHMENT_SIZE_LIMITS = {
  'image/png': 5 * MB,
  'image/jpeg': 5 * MB,
  'video/mp4': 16 * MB,
  'application/pdf': 10 * MB,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    10 * MB,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 10 * MB,
};
const FILE_ICONS = {
  'application/pdf': 'i-lucide-file-text',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'i-lucide-file-text',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    'i-lucide-file-spreadsheet',
};

const formState = {
  uiFlags: useMapGetter('captainResponses/getUIFlags'),
};

const initialState = {
  question: '',
  answer: '',
  assignUrls: false,
  actionUrl: '',
  mediaUrl: '',
  sharePolicy: 'always',
  attachFile: false,
  newFiles: [],
  removedAttachmentIds: [],
};

const SHARE_POLICY_OPTIONS = [
  {
    value: 'always',
    label: t('CAPTAIN.RESPONSES.FORM.SHARE_POLICY.OPTIONS.ALWAYS'),
  },
  {
    value: 'first_time_per_conversation',
    label: t(
      'CAPTAIN.RESPONSES.FORM.SHARE_POLICY.OPTIONS.FIRST_TIME_PER_CONVERSATION'
    ),
  },
  {
    value: 'on_request_only',
    label: t('CAPTAIN.RESPONSES.FORM.SHARE_POLICY.OPTIONS.ON_REQUEST_ONLY'),
  },
];

const state = reactive({ ...initialState });
const fileInputRef = ref(null);
const existingAttachments = ref([]);

const validationRules = {
  question: { required, minLength: minLength(1) },
  answer: { required, minLength: minLength(1) },
  actionUrl: { url },
  mediaUrl: { url },
};

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() => formState.uiFlags.value.creatingItem);
const showUpdateConfirmation = ref(false);

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`CAPTAIN.RESPONSES.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  question: getErrorMessage('question', 'QUESTION'),
  answer: getErrorMessage('answer', 'ANSWER'),
  actionUrl: getErrorMessage('actionUrl', 'ACTION_URL'),
  mediaUrl: getErrorMessage('mediaUrl', 'MEDIA_URL'),
}));

const handleCancel = () => emit('cancel');

const attachmentItems = computed(() => [
  ...existingAttachments.value
    .filter(file => !state.removedAttachmentIds.includes(file.id))
    .map(file => ({
      key: `existing-${file.id}`,
      id: file.id,
      name: file.filename,
      type: file.file_type,
      size: file.byte_size,
      previewUrl: file.file_url,
    })),
  ...state.newFiles.map(({ file, previewUrl }) => ({
    key: `new-${previewUrl}`,
    name: file.name,
    type: file.type,
    size: file.size,
    previewUrl,
    file,
  })),
]);

const canAddMoreFiles = computed(
  () => attachmentItems.value.length < MAX_ATTACHMENTS
);

const formatFileSize = bytes =>
  bytes >= MB
    ? `${(bytes / MB).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const handleFileChange = event => {
  const incoming = Array.from(event.target.files || []);
  event.target.value = '';

  incoming.forEach(file => {
    if (!canAddMoreFiles.value) {
      useAlert(
        t('CAPTAIN.RESPONSES.FORM.ATTACHMENT.TOO_MANY', {
          max: MAX_ATTACHMENTS,
        })
      );
      return;
    }
    if (!ATTACHMENT_SIZE_LIMITS[file.type]) {
      useAlert(t('CAPTAIN.RESPONSES.FORM.ATTACHMENT.INVALID_TYPE'));
      return;
    }
    if (file.size > ATTACHMENT_SIZE_LIMITS[file.type]) {
      useAlert(t('CAPTAIN.RESPONSES.FORM.ATTACHMENT.TOO_LARGE'));
      return;
    }
    state.newFiles = [
      ...state.newFiles,
      { file, previewUrl: URL.createObjectURL(file) },
    ];
  });
};

const openFileDialog = () => {
  nextTick(() => {
    fileInputRef.value?.click();
  });
};

const removeFile = item => {
  if (item.file) {
    URL.revokeObjectURL(item.previewUrl);
    state.newFiles = state.newFiles.filter(({ file }) => file !== item.file);
    return;
  }
  state.removedAttachmentIds = [...state.removedAttachmentIds, item.id];
};

const hasFormDataPayload = computed(
  () =>
    state.attachFile &&
    (state.newFiles.length > 0 || state.removedAttachmentIds.length > 0)
);

const hasShareableContent = computed(
  () => state.assignUrls || state.attachFile
);

const prepareJsonPayload = () => ({
  assistant_response: {
    question: state.question,
    answer: state.answer,
    action_url: state.assignUrls ? state.actionUrl : '',
    media_url: state.assignUrls ? state.mediaUrl : '',
    ...(hasShareableContent.value && { share_policy: state.sharePolicy }),
    ...(props.mode === 'edit' && { force_regenerate: true }),
  },
});

const prepareFormDataPayload = () => {
  const formData = new FormData();
  formData.append('assistant_response[question]', state.question);
  formData.append('assistant_response[answer]', state.answer);
  formData.append(
    'assistant_response[action_url]',
    state.assignUrls ? state.actionUrl : ''
  );
  formData.append(
    'assistant_response[media_url]',
    state.assignUrls ? state.mediaUrl : ''
  );
  if (hasShareableContent.value)
    formData.append('assistant_response[share_policy]', state.sharePolicy);
  if (props.mode === 'edit')
    formData.append('assistant_response[force_regenerate]', 'true');
  state.newFiles.forEach(({ file }) =>
    formData.append('assistant_response[attachments][]', file)
  );
  state.removedAttachmentIds.forEach(id =>
    formData.append('remove_attachment_ids[]', id)
  );
  return formData;
};

const submitForm = () => {
  emit(
    'submit',
    hasFormDataPayload.value ? prepareFormDataPayload() : prepareJsonPayload()
  );
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  if (props.mode === 'edit') {
    showUpdateConfirmation.value = true;
    return;
  }

  submitForm();
};

const confirmUpdate = () => {
  showUpdateConfirmation.value = false;
  submitForm();
};

const cancelUpdate = () => {
  showUpdateConfirmation.value = false;
};

const updateStateFromResponse = response => {
  if (!response) return;

  const {
    question,
    answer,
    action_url: actionUrl,
    media_url: mediaUrl,
    sharePolicy,
    attachments,
  } = response;

  Object.assign(state, {
    question,
    answer,
    assignUrls: !!(actionUrl || mediaUrl),
    actionUrl: actionUrl || '',
    mediaUrl: mediaUrl || '',
    sharePolicy: sharePolicy || 'always',
    attachFile: !!attachments?.length,
    newFiles: [],
    removedAttachmentIds: [],
  });
  existingAttachments.value = attachments || [];
};

watch(
  () => props.response,
  newResponse => {
    if (props.mode === 'edit' && newResponse) {
      updateStateFromResponse(newResponse);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() =>
  state.newFiles.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl))
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.question"
      :label="t('CAPTAIN.RESPONSES.FORM.QUESTION.LABEL')"
      :placeholder="t('CAPTAIN.RESPONSES.FORM.QUESTION.PLACEHOLDER')"
      :message="formErrors.question"
      :message-type="formErrors.question ? 'error' : 'info'"
    />
    <Editor
      v-model="state.answer"
      :label="t('CAPTAIN.RESPONSES.FORM.ANSWER.LABEL')"
      :placeholder="t('CAPTAIN.RESPONSES.FORM.ANSWER.PLACEHOLDER')"
      :message="formErrors.answer"
      :max-length="10000"
      :message-type="formErrors.answer ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-3">
        <Switch v-model="state.assignUrls" />
        <span class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.RESPONSES.FORM.ASSIGN_URLS.LABEL') }}
        </span>
      </div>
      <template v-if="state.assignUrls">
        <Input
          v-model="state.actionUrl"
          :label="t('CAPTAIN.RESPONSES.FORM.ACTION_URL.LABEL')"
          :placeholder="t('CAPTAIN.RESPONSES.FORM.ACTION_URL.PLACEHOLDER')"
          :message="formErrors.actionUrl"
          :message-type="formErrors.actionUrl ? 'error' : 'info'"
        />
        <Input
          v-model="state.mediaUrl"
          :label="t('CAPTAIN.RESPONSES.FORM.MEDIA_URL.LABEL')"
          :placeholder="t('CAPTAIN.RESPONSES.FORM.MEDIA_URL.PLACEHOLDER')"
          :message="formErrors.mediaUrl"
          :message-type="formErrors.mediaUrl ? 'error' : 'info'"
        />
      </template>
    </div>

    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-3">
        <Switch v-model="state.attachFile" />
        <span class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.RESPONSES.FORM.ATTACH_FILE.LABEL') }}
        </span>
      </div>
      <div v-if="state.attachFile" class="flex flex-col gap-2">
        <input
          ref="fileInputRef"
          type="file"
          multiple
          accept=".png,.jpg,.jpeg,.mp4,.pdf,.docx,.xlsx"
          class="hidden"
          @change="handleFileChange"
        />
        <div
          class="grid gap-2"
          :class="attachmentItems.length ? 'grid-cols-3' : 'grid-cols-1'"
        >
          <div
            v-for="item in attachmentItems"
            :key="item.key"
            class="flex flex-col gap-1.5 p-1.5 rounded-lg bg-n-alpha-2 min-w-0"
          >
            <div
              class="flex items-center justify-center w-full overflow-hidden rounded-md aspect-video bg-n-slate-3"
            >
              <img
                v-if="item.type?.startsWith('image/')"
                :src="item.previewUrl"
                :alt="item.name"
                class="object-cover w-full h-full"
              />
              <video
                v-else-if="item.type?.startsWith('video/')"
                :src="item.previewUrl"
                preload="metadata"
                muted
                class="object-cover w-full h-full"
              />
              <i
                v-else
                class="text-2xl text-n-slate-11"
                :class="FILE_ICONS[item.type] || 'i-lucide-file'"
              />
            </div>
            <p
              class="m-0 text-xs font-medium truncate text-n-slate-12"
              :title="item.name"
            >
              {{ item.name }}
            </p>
            <span class="text-xs text-n-slate-11">
              {{ formatFileSize(item.size) }}
            </span>
            <Button
              type="button"
              variant="link"
              color="ruby"
              size="xs"
              :label="t('CAPTAIN.RESPONSES.FORM.ATTACHMENT.REMOVE')"
              class="self-start"
              @click="removeFile(item)"
            />
          </div>
          <button
            v-if="canAddMoreFiles"
            type="button"
            class="flex flex-col items-center justify-center gap-1 p-3 border border-dashed rounded-lg text-n-slate-11 border-n-strong hover:bg-n-alpha-2"
            :class="{ 'aspect-video': attachmentItems.length }"
            @click="openFileDialog"
          >
            <i class="text-xl i-lucide-upload" />
            <span class="text-xs">
              {{ t('CAPTAIN.RESPONSES.FORM.ATTACHMENT.CHOOSE_FILE') }}
            </span>
          </button>
        </div>
        <span class="text-xs text-n-slate-11">
          {{
            t('CAPTAIN.RESPONSES.FORM.ATTACHMENT.HINT', {
              max: MAX_ATTACHMENTS,
            })
          }}
        </span>
      </div>
    </div>

    <div v-if="hasShareableContent" class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.RESPONSES.FORM.SHARE_POLICY.LABEL') }}
      </label>
      <span class="text-xs text-n-slate-11">
        {{ t('CAPTAIN.RESPONSES.FORM.SHARE_POLICY.HINT') }}
      </span>
      <Select
        v-model="state.sharePolicy"
        :options="SHARE_POLICY_OPTIONS"
        class="!w-full [&>select]:w-full"
      />
    </div>

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        :disabled="showUpdateConfirmation"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t(`CAPTAIN.FORM.${mode.toUpperCase()}`)"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading || showUpdateConfirmation"
      />
    </div>
    <div
      v-if="showUpdateConfirmation"
      class="flex flex-col gap-3 p-3 rounded-lg bg-n-alpha-2"
    >
      <p class="mb-0 text-sm text-n-slate-12">
        {{ t('CAPTAIN.RESPONSES.FORM.UPDATE_CONFIRMATION.MESSAGE') }}
      </p>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          type="button"
          color="ruby"
          icon="i-lucide-x"
          :label="t('CAPTAIN.RESPONSES.FORM.UPDATE_CONFIRMATION.CANCEL')"
          class="w-full"
          @click="cancelUpdate"
        />
        <Button
          type="button"
          color="teal"
          icon="i-lucide-check"
          :label="t('CAPTAIN.RESPONSES.FORM.UPDATE_CONFIRMATION.CONFIRM')"
          class="w-full"
          :is-loading="isLoading"
          :disabled="isLoading"
          @click="confirmUpdate"
        />
      </div>
    </div>
  </form>
</template>
